<template>
    <tr>
        <td class="text-center">
            <span class="ix-drag-icon handle-line"></span>
        </td>
        <td>
            <input type="text" class="form-control form-control-sm" :class="{ 'is-invalid': articleError }"
                   v-model="component.label" :id="'kitComponentAutocomplete' + component.uniq"
                   placeholder="Rechercher un article…" autocomplete="off">
            <input type="hidden" :name="'components[' + index + '][article_id]'" :value="component.article_id">
            <div class="invalid-feedback" v-if="articleError">{{ articleError }}</div>
            <span v-if="component.article_id && !component.is_active" class="d-block ix-text-small ix-text-orange">
                <i class="far fa-moon"></i> En sommeil
            </span>
        </td>
        <td>
            <input type="number" step="0.001" min="0.001" class="form-control form-control-sm text-right"
                   :class="{ 'is-invalid': quantityError }"
                   :name="'components[' + index + '][quantity]'" v-model="component.quantity"
                   :required="!!component.article_id">
            <div class="invalid-feedback" v-if="quantityError">{{ quantityError }}</div>
        </td>
        <td class="align-middle">{{ component.article_id ? (component.unit || 'U') : '' }}</td>
        <td class="text-right align-middle">
            <template v-if="component.article_id">
                <span v-if="component.is_manage">{{ number(component.available_stock) }} {{ component.unit || 'U' }}</span>
                <span v-else class="ix-text-secondary" v-b-tooltip title="Ne limite pas le nombre de kits">Non géré</span>
            </template>
        </td>
        <td class="text-right align-middle ix-col-group-start">
            <template v-if="component.article_id">
                <span v-if="component.unit_cost !== null">{{ currency(component.unit_cost) }}</span>
                <span v-else class="ix-text-orange" v-b-tooltip title="Aucun prix d'achat">-</span>
            </template>
        </td>
        <td class="text-right align-middle">
            <span v-if="lineCost !== null">{{ currency(lineCost) }}</span>
        </td>
        <td class="text-right align-middle ix-col-group-start">
            <span v-if="component.article_id && component.selling_price !== null">{{ currency(component.selling_price) }}</span>
        </td>
        <td class="text-right align-middle">
            <span v-if="lineSelling !== null">{{ currency(lineSelling) }}</span>
        </td>
        <td class="text-right">
            <span class="btn btn-sm transparent" @click="emitEventLineDeleted" v-if="component.article_id || !isLast">
                <i class="far fa-trash"></i>
            </span>
        </td>
    </tr>
</template>

<script>
    import $ from 'jquery'
    import Swal from "sweetalert2";

    /**
     * Une ligne de la composition d'un kit : le composant, sa quantité par kit, et ses
     * repères de coût et de prix.
     *
     * @see docs/DRI/Phase2/articles-composes.md § 8.2
     */
    export default {
        name: 'article-kit-component-form',
        props: ['component', 'index', 'isLast', 'autocompleteRoute', 'excludedIds', 'errors'],
        computed: {
            quantity() {
                const quantity = parseFloat(this.component.quantity);

                return isNaN(quantity) ? 0 : quantity;
            },
            lineCost() {
                if (!this.component.article_id || this.component.unit_cost === null) return null;

                return this.quantity * this.component.unit_cost;
            },
            lineSelling() {
                if (!this.component.article_id || this.component.selling_price === null) return null;

                return this.quantity * this.component.selling_price;
            },
            articleError() {
                return this.firstError('components.' + this.index + '.article_id');
            },
            quantityError() {
                return this.firstError('components.' + this.index + '.quantity');
            },
        },
        methods: {
            firstError(key) {
                return this.errors && this.errors[key] ? this.errors[key][0] : null;
            },
            currency(value) {
                return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(value || 0);
            },
            /** Une quantité sans décimales inutiles : « 68 » et non « 68,000 ». */
            number(value) {
                return (parseFloat(value) || 0).toFixed(3).replace(/\.?0+$/, '').replace('.', ',');
            },
            select(item) {
                const label = (item.number ? item.number + ' - ' : '') + item.name;

                Object.assign(this.component, {
                    article_id: item.id,
                    label: label,
                    selected_label: label,
                    unit: item.unit,
                    available_stock: item.available_stock,
                    // Une information absente ne dit rien : ni « en sommeil », ni un prix à zéro.
                    is_manage: item.is_manage !== false,
                    is_active: item.is_active !== false,
                    unit_cost: item.unit_cost === undefined ? null : item.unit_cost,
                    selling_price: item.selling_price === undefined ? null : item.selling_price,
                });

                if (!this.component.quantity) {
                    this.component.quantity = 1;
                }

                this.$emit('component-selected', this.index);
            },
            emitEventLineDeleted() {
                // Une ligne vide s'enlève sans confirmation.
                if (!this.component.article_id) {
                    this.$emit('component-deleted', this.index);

                    return;
                }

                Swal.fire({
                    title: 'Retirer ce composant ?',
                    text: '',
                    icon: 'warning',
                    showCancelButton: true,
                    cancelButtonText: 'Annuler',
                    confirmButtonColor: '#D15240',
                    confirmButtonText: 'Oui, retirer',
                    reverseButtons: true,
                    heightAuto: false
                }).then((result) => {
                    if (result.value) {
                        this.$emit('component-deleted', this.index);
                    }
                });
            },
        },
        mounted() {
            const input = $('#kitComponentAutocomplete' + this.component.uniq);

            input.autocomplete({
                minLength: 0,
                source: (request, response) => {
                    $.ajax({
                        url: this.autocompleteRoute,
                        type: 'get',
                        dataType: 'json',
                        headers: this.getHttpJsonRequestHeaders().headers,
                        // Le kit lui-même et les composants déjà choisis ne sont pas reproposés.
                        data: { q: request.term, '!id': this.excludedIds.join(';') },
                        success: (data) => {
                            response($.map(data.data, (item) => {
                                return Object.assign(item, {
                                    label: (item.number ? item.number + ' - ' : '') + item.name,
                                    value: (item.number ? item.number + ' - ' : '') + item.name,
                                });
                            }));
                        }
                    });
                },
                change: (e, ui) => {
                    // Un libellé retapé sans choisir d'article ne désigne plus aucun composant.
                    if (!ui.item && this.component.label !== this.component.selected_label) {
                        this.component.article_id = null;
                    }
                },
                select: (e, ui) => {
                    this.select(ui.item);
                }
            }).focus(() => {
                input.autocomplete('search', input.val());
            });
        }
    }
</script>
